import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Bell, Search, Menu, User, Check, Settings as SettingsIcon, LogOut, LogIn, Users, MessageSquare, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { cn, getRoomPath, getCommunityPath } from '../utils';
import { api } from '../api';

import { canManagePlatform } from '../utils/permissions';

const Navbar = ({ onMenuClick, hasCustomSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { communities, rooms } = useActivities();
  const isPlatformAdmin = canManagePlatform(user);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleBlur = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearchFocused(false);
    }, 200);
  };

  const handleSearchKeyDown = (e) => {
    if (!searchQuery.trim() || searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      const result = searchResults[focusedIndex];
      if (result) {
        setIsMobileSearchOpen(false);
        setIsSearchFocused(false);
        navigate(result.path);
      }
    } else if (e.key === 'Escape') {
      setIsSearchFocused(false);
      searchInputRef.current?.blur();
    }
  };

  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!user) return;
    
    const fetchNotificationCount = async () => {
      try {
        const data = await api.get('/notifications?unread=1');
        const validData = Array.isArray(data) ? data : [];
        setUnreadCount(validData.length);
        setNotifications(validData.slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch notification count', err);
      }
    };

    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      setIsMobileSearchOpen(false);
      setIsSearchFocused(false);
      navigate(`/explore?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => Array.isArray(prev) ? prev.map(n => ({ ...n, is_read: true })) : []);
    } catch (err) {
      console.error('Failed to mark all as read', err);
    }
  };

  const notificationConfig = {
    live: {
      label: 'Live',
      icon: null,
      bg: 'bg-red-50',
      text: 'text-red-500',
    },
    badge: {
      label: 'Achievement',
      icon: Check,
      bg: 'bg-yellow-50',
      text: 'text-yellow-600',
    },
    qa: {
      label: 'Q&A Reply',
      icon: MessageSquare,
      bg: 'bg-brand-indigo/5',
      text: 'text-brand-indigo',
    },
    invite: {
      label: 'Community Invite',
      icon: Users,
      bg: 'bg-brand-indigo/10',
      text: 'text-brand-indigo',
    },
  };

  const searchResults = searchQuery ? [
    ...(Array.isArray(rooms) ? rooms : []).filter(r => (r.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
      .map(r => ({ ...r, resultType: 'room', path: getRoomPath(r) })),
    ...(Array.isArray(communities) ? communities : []).filter(c => (c.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
      .map(c => ({ ...c, resultType: 'community', path: getCommunityPath(c) }))
  ].slice(0, 5) : [];

  const SearchResultItem = ({ result, onClick, isActive }) => (
    <Link 
      to={result.path} 
      onMouseDown={onClick}
      className={cn(
        "flex items-center gap-3 p-2.5 rounded-xl transition-all group",
        isActive ? "bg-brand-indigo/5 ring-1 ring-brand-indigo/20" : "hover:bg-gray-50"
      )}
    >
      <div className={cn(
        "w-10 h-10 rounded-lg flex items-center justify-center font-bold shrink-0 shadow-sm transition-transform",
        isActive && "scale-105",
        result.resultType === 'room' ? "bg-red-50 text-red-500" : "bg-brand-indigo/10 text-brand-indigo"
      )}>
        {result.resultType === 'room' ? (
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span>
        ) : (
          (result.icon || result.name || '').substring(0, 2).toUpperCase()
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-gray-900 truncate group-hover:text-brand-indigo transition-colors tracking-tight">{result.title || result.name}</p>
        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.15em]">
          {result.resultType} • {result.participants || result.members_count || 0} {result.resultType === 'room' ? 'watching' : 'members'}
        </p>
      </div>
    </Link>
  );

  const SearchResultsDropdown = ({ results, isMobile, onClose }) => (
    <div className={cn(
      "absolute top-full mt-2 bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden py-4 z-[100] transition-all duration-200 animate-in slide-in-from-top-1",
      isMobile ? "left-0 w-full" : "right-0 w-[400px] max-w-[calc(100vw-2rem)]"
    )}>
      <div className="px-4 mb-4">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2 px-2">
          {results.length > 0 ? 'Top Results' : 'No Results'}
        </h3>
        <div className="space-y-1">
          {results.length > 0 ? (
            results.map((result, index) => (
              <SearchResultItem 
                key={`${result.resultType}-${result.id}`} 
                result={result} 
                onClick={onClose} 
                isActive={index === focusedIndex}
              />
            ))
          ) : (
            <div className="py-8 text-center">
              <Search size={32} className="mx-auto mb-2 text-gray-200" />
              <p className="text-sm font-medium text-gray-400">No rooms or communities found for "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
      {results.length > 0 && (
        <div className="px-4 border-t border-gray-50 pt-4 bg-gray-50/50">
          <button 
            type="button"
            onClick={handleSearchSubmit}
            className="w-full text-center text-[10px] font-bold text-brand-indigo hover:underline transition-all uppercase tracking-[0.15em]"
          >
            See all results for "{searchQuery}"
          </button>
        </div>
      )}
    </div>
  );

  const NotificationPreview = ({ notification }) => {
    const config = notificationConfig[notification.type] || notificationConfig.invite;
    const Icon = config.icon;

    return (
      <Link
        to={notification.path}
        className="w-full text-left flex items-start gap-4 p-3 hover:bg-gray-50 rounded-2xl transition-all group"
      >
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-105",
          config.bg,
          config.text
        )}>
          {notification.type === 'live' ? (
            <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
          ) : (
            Icon && <Icon size={20} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1.5">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate group-hover:text-brand-indigo transition-colors tracking-tight">{notification.title}</p>
              <p className="text-xs text-gray-500 font-medium leading-relaxed line-clamp-2">{notification.desc}</p>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 shrink-0">{notification.time}</span>
          </div>
          <span className={cn(
            "inline-flex px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] shadow-sm",
            notification.type === 'live' ? 'bg-red-500 text-white' :
            notification.type === 'badge' ? 'bg-yellow-500 text-white' :
            notification.type === 'qa' ? 'bg-brand-indigo text-white' :
            'bg-brand-indigo text-white'
          )}>
            {config.label}
          </span>
        </div>
      </Link>
    );
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Home';
    if (path === '/communities') return 'Communities';
    if (path === '/explore') return 'Explore';
    if (path === '/live') return 'Live Sessions';
    if (path === '/saved') return 'Saved Items';
    if (path === '/notifications') return 'Notifications';
    if (path === '/profile') return 'My Profile';
    if (path === '/settings') return 'Account Settings';
    if (path === '/about') return 'About Us';
    if (path === '/contact') return 'Contact Support';
    
    if (path.startsWith('/c/')) {
      if (path.endsWith('/settings')) return 'Community Settings';
      if (path.endsWith('/r/new')) return 'New Resource';
      if (path.endsWith('/d/new')) return 'New Discussion';
      return 'Community Hub';
    }
    
    const roomTypeMap = {
      '/rm/lv/new': 'Create Live Session',
      '/rm/bs/new': 'Start Brainstorming',
      '/rm/pl/new': 'Create Poll',
      '/rm/new': 'Create Room',
      '/rm/lv/': 'Live Session',
      '/rm/bs/': 'Ideation Hub',
      '/rm/pl/': 'Live Poll',
      '/rm/': 'Live Room'
    };

    for (const [prefix, title] of Object.entries(roomTypeMap)) {
      if (path.startsWith(prefix)) return title;
    }
    
    if (path.startsWith('/admin')) {
      const sub = path.split('/')[2];
      if (!sub) return 'Admin Dashboard';
      return `Admin: ${sub.charAt(0).toUpperCase() + sub.slice(1).replace(/-/g, ' ')}`;
    }
    
    const segment = path.split('/')[1];
    if (!segment) return 'Chooziq';
    
    return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
  };

  return (
    <header className="sticky top-0 z-[80] w-full bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-4 md:px-8">
        <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
          {!isMobileSearchOpen ? (
            <>
              {hasCustomSidebar && (
                <button 
                  onClick={onMenuClick}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-600 mr-1"
                  aria-label="Open community menu"
                >
                  <Menu size={20} />
                </button>
              )}
              <Link to="/" className="flex items-center gap-2 mr-2 md:mr-4 shrink-0">
                <img src="/logo192.png" alt="Chooziq Logo" className="w-8 h-8 rounded-lg shadow-lg object-contain" />
                <span className="text-lg font-bold tracking-tight text-brand-indigo hidden xs:block">Chooziq.com</span>
              </Link>

              {!isHome && (
                <button 
                  onClick={() => navigate(-1)}
                  className="p-1.5 md:p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-600 group flex items-center gap-2 shrink-0"
                  aria-label="Go back"
                >
                  <ArrowLeft size={18} className="group-hover:-translate-x-0.5 transition-transform md:w-5 md:h-5" />
                </button>
              )}
              
              <span className={cn(
                "text-sm font-bold text-gray-900 lg:text-xl tracking-tight truncate transition-all duration-200",
                !isHome ? "block sm:hidden lg:block" : "hidden"
              )}>
                {getPageTitle()}
              </span>
            </>
          ) : (
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 w-full transition-all duration-200">
              <button 
                type="button"
                onClick={() => setIsMobileSearchOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-xl text-gray-500"
                aria-label="Close search"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  autoFocus
                  type="text" 
                  placeholder="Search anything..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={handleBlur}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full pl-10 pr-10 py-2 bg-gray-50 border-gray-100 focus:bg-white focus:border-brand-indigo/30 focus:ring-4 focus:ring-brand-indigo/5 rounded-2xl text-sm transition-all outline-none border"
                />
                
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
                
                {/* Mobile Search Results */}
                {(isSearchFocused && searchQuery.trim()) && (
                  <SearchResultsDropdown 
                    results={searchResults} 
                    isMobile={true} 
                    onClose={() => setIsMobileSearchOpen(false)} 
                  />
                )}
              </div>
            </form>
          )}
        </div>
        
        <div className={cn(
          "flex items-center gap-2 md:gap-4 ml-2 md:ml-4 min-w-0",
          isMobileSearchOpen && "hidden"
        )}>
          <div className="hidden sm:flex items-center sm:relative group">
              <form onSubmit={handleSearchSubmit} className="relative flex items-center">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-indigo transition-colors" size={18} />
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder="Search anything..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={handleBlur}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-10 pr-10 py-2 bg-gray-50 border-gray-200 focus:bg-white focus:border-brand-indigo/30 focus:ring-4 focus:ring-brand-indigo/5 rounded-2xl text-sm w-48 focus:w-64 lg:w-80 lg:focus:w-[400px] transition-all duration-300 outline-none border shadow-sm"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
                    >
                      <X size={14} />
                    </button>
                  ) : (
                    <kbd className="hidden lg:flex h-5 w-5 items-center justify-center rounded border border-gray-200 bg-white text-[10px] font-bold text-gray-400 shadow-sm pointer-events-none">
                      /
                    </kbd>
                  )}
                </div>
              </form>
              
              {/* Search Results Dropdown */}
              {(isSearchFocused && searchQuery.trim()) && (
                <SearchResultsDropdown 
                  results={searchResults} 
                  isMobile={false} 
                  onClose={() => setIsSearchFocused(false)} 
                />
              )}
            </div>
          
          <div className="flex items-center gap-1 md:gap-4">
            <button 
              onClick={() => setIsMobileSearchOpen(true)}
              className="sm:hidden p-2 text-gray-500 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <div className="relative flex items-center">
                  <button 
                    onClick={() => setShowNotifications(!showNotifications)}
                    className={cn(
                      "p-2 rounded-xl transition-all relative",
                      showNotifications ? "bg-brand-indigo text-white" : "text-gray-500 hover:text-brand-indigo hover:bg-brand-indigo/5"
                    )}
                    aria-label="Toggle notifications"
                    aria-expanded={showNotifications}
                  >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-[2px] -right-[2px] min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center shadow-sm">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="absolute top-full mt-2 right-0 w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden py-4 transition-all duration-200 z-[90] animate-in slide-in-from-top-1 origin-top-right">
                      <div className="px-6 mb-4 flex items-center justify-between">
                        <div>
                          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Notifications</h3>
                          <p className="text-xs text-gray-400 font-semibold mt-0.5">Stay updated with latest activity</p>
                        </div>
                        {unreadCount > 0 && (
                          <button 
                            onClick={markAllAsRead}
                            className="p-2 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all"
                            title="Mark all as read"
                          >
                            <Check size={16} />
                          </button>
                        )}
                      </div>
                      <div className="max-h-[380px] overflow-y-auto px-3 space-y-2 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        {(Array.isArray(notifications) && notifications.length > 0) ? (
                          notifications.map((notification, index) => (
                            <NotificationPreview
                              key={notification.id}
                              notification={{
                                ...notification,
                                desc: notification.description,
                                time: 'New'
                              }}
                            />
                          ))
                        ) : (
                          <div className="py-12 text-center text-gray-300">
                            <Bell size={32} className="mx-auto mb-2 opacity-20" />
                            <p className="text-xxs font-bold uppercase tracking-widest">No new notifications</p>
                          </div>
                        )}
                      </div>
                      <div className="px-6 border-t border-gray-50 pt-4 bg-gray-50/50">
                        <Link 
                          to="/notifications" 
                          onClick={() => setShowNotifications(false)}
                          className="block w-full text-center text-xs font-bold text-gray-400 hover:text-brand-indigo transition-all uppercase tracking-widest"
                        >
                          View all inbox
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
            
            <div className="h-8 w-[1px] bg-gray-100 mx-2 hidden md:block"></div>
            
            {user ? (
              <>
                <Link 
                  to={isPlatformAdmin ? "/admin" : "/profile"} 
                  className="flex-shrink-0 flex items-center gap-2 p-1 hover:bg-gray-50 rounded-2xl transition-all group"
                >
                  <div className="w-8 h-8 rounded-xl bg-brand-indigo flex items-center justify-center text-white shadow-lg shadow-brand-indigo/20 overflow-hidden relative">
                    {user.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={user.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(user.name)}`;
                        }}
                      />
                    ) : (
                      <User size={16} />
                    )}
                  </div>
                  <span className="hidden lg:block text-sm font-bold text-gray-700">{user.name}</span>
                </Link>
                
                <button 
                  onClick={logout}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" 
                  title="Logout"
                  aria-label="Logout"
                >
                  <LogOut size={20} />
                </button>

                <Link 
                  to={isPlatformAdmin ? "/admin/settings" : "/settings"} 
                  className="p-2 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all" 
                  title="Settings"
                  aria-label="Settings"
                >
                  <SettingsIcon size={20} />
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="hidden sm:flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-brand-indigo font-bold text-sm transition-all"
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="flex items-center gap-2 px-4 py-2 bg-brand-indigo text-white rounded-xl text-sm font-bold hover:bg-brand-indigo/90 transition-all shadow-lg shadow-brand-indigo/20"
                >
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
